import React, { useState, useCallback, useEffect } from "react";

import _ from "lodash";
import { notification } from "antd";

import { useRequest, useLazyRequest } from "../../../hooks/useRequest.hook";
import {
  KontaxTransactionFilterOptions,
  ITransaction,
  KontaxTransactionMeta,
  TransactionSplit,
  Asset,
  AccountOption,
} from "../../../../types";
import api from "../../../../api";
import { TransactionsListViewWrapper } from "./styledComponents";
import { TransactionSplitState } from "../../../common/types";
import {
  destroyMessage,
  showSuccessMessage,
  showLoadingMessage,
} from "../../../../utils";
import { getServerErrorNotification } from "../../../../utils/error";
import TransactionsTable from "./TransactionsTable";
import { buildRequestFilters } from "./buildRequestFilters";
import {
  KontaxTransactionUpsertInput,
  useUpsertTransactionsMutation,
} from "../../../../api/graphql/mutations/transaction/upsertKontaxTransactions";
import { hasVerifiedOnly } from "./utils";
import { verifyTransactions } from "./verifyTransactions";
import { shouldRefetchTransactionOnAssetUpload } from "../../TransactionView/helpers";
import { DEFAULT_PAGE_SIZE } from "./constants";
import {
  canFetchDepreciationRowsForFilters,
  getYearsWithYearEndInDateFilter,
  isDepreciationRow,
  mapDepreciationRowsToTransactions,
  mergeDepreciationRowsIntoTransactions,
  shouldFetchDepreciationRowsForPage,
} from "./depreciationRows";

const DEFAULT_SORT = {
  field: "valuta_date",
  direction: "DESC",
};

interface Props {
  filterOptions: KontaxTransactionFilterOptions;
  isNested?: boolean;
  currencyOptions?: string[];
  accountOptions?: AccountOption[];
}

const TransactionList = ({
  filterOptions,
  isNested,
  currencyOptions = [],
  accountOptions = [],
}: Props) => {
  // Map external_account_id -> account (institution name/logo) so the Bank
  // column can render each row's logo without an extra per-row lookup.
  const accountsById = React.useMemo(
    () =>
      new Map(
        accountOptions.map((account) => [account.externalAccountId, account])
      ),
    [accountOptions]
  );
  const [response, setResponse] = useState<{
    transactions: ITransaction[] | null;
    count?: number;
  }>({
    transactions: null,
    count: undefined,
  });

  const [pagination, setPagination] = useState<{
    page: number;
    pageSize: number;
  }>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [sort, setSort] = useState<
    | {
        field: string;
        direction: string;
      }
    | undefined
  >(DEFAULT_SORT);

  const [assetsFilter, setAssetsFilter] = useState<boolean | undefined>(
    filterOptions.assets
  );

  const [noteFilters, setNoteFilters] = useState<{
    internalNote: boolean | null;
    personalNote: boolean | null;
  }>({
    internalNote: filterOptions.internalNote ?? null,
    personalNote: filterOptions.personalNote ?? null,
  });

  const [verifiedFilter, setVerifiedFilter] = useState<boolean | undefined>(
    filterOptions.verified
  );

  const [verifiedByMLFilter, setVerifiedByMLFilter] = useState<
    boolean | undefined
  >(filterOptions.verifiedByML);

  const [submissionStatusFilter, setSubmissionStatusFilter] = useState<
    string | null
  >(filterOptions.submissionStatus ?? null);

  // Currency is a column-header filter (like verified/submitted): it lives in
  // local state and is not persisted to the URL.
  const [currencyFilter, setCurrencyFilter] = useState<string | undefined>(
    undefined
  );

  const [selectedTransactions, setSelectedTransactions] = useState<
    ITransaction[]
  >([]);

  const [
    hasSelectedVerifiedTransactionsOnly,
    setHasSelectedVerifiedTransactionsOnly,
  ] = useState<boolean>(false);

  useEffect(() => {
    setPagination((prev) => {
      if (prev.page === 1) {
        return prev;
      }
      return { ...prev, page: 1 };
    });
  }, [filterOptions]);

  // Reset the currency filter when switching customers: the previous selection
  // may not exist for the new customer, which would show a dead selection and
  // return an empty list.
  useEffect(() => {
    setCurrencyFilter(undefined);
  }, [filterOptions.email]);

  const resetSelectedTransactions = () => {
    setSelectedTransactions([]);
    setHasSelectedVerifiedTransactionsOnly(false);
  };

  const setSelection = (selectedTransactionIds: string[]) => {
    const transactions = response.transactions;

    if (transactions) {
      const selectedTransactions = transactions.filter(
        (t) => selectedTransactionIds.includes(t.id) && !isDepreciationRow(t)
      );
      setSelectedTransactions(selectedTransactions);

      setHasSelectedVerifiedTransactionsOnly(
        hasVerifiedOnly(selectedTransactions)
      );
    }
  };

  const verifySelectedTransactions = async () =>
    await verifyTransactions({
      selectedTransactions,
      updateTransactionsMeta,
      resetSelectedTransactions,
    });

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setPagination((prev) => {
        if (prev.page === page && prev.pageSize === pageSize) {
          return prev;
        }
        return { page, pageSize };
      });
    },
    []
  );

  const requestFilters = React.useMemo(
    () =>
      buildRequestFilters({
        filterOptions,
        sort,
        assetsFilter,
        noteFilters,
        verifiedFilter,
        verifiedByMLFilter,
        submissionStatusFilter,
        currencyFilter,
      }),
    [
      filterOptions,
      sort,
      assetsFilter,
      noteFilters,
      verifiedFilter,
      verifiedByMLFilter,
      submissionStatusFilter,
      currencyFilter,
    ]
  );

  const fetchTransactions = useCallback(async () => {
    if (filterOptions.email) {
      const email = filterOptions.email;
      const loadingKey = "loading-verified-transactions";
      try {
        showLoadingMessage(loadingKey);
        resetSelectedTransactions();

        const data = await api.kontax.fetchTransactions(
          requestFilters,
          undefined,
          {
            page: pagination.page,
            pageSize: pagination.pageSize,
          }
        );

        const shouldFetchDepreciationRows =
          canFetchDepreciationRowsForFilters(requestFilters) &&
          shouldFetchDepreciationRowsForPage({
            page: pagination.page,
            pageSize: pagination.pageSize,
            totalCount: data.count ?? 0,
            sort,
          });
        const yearsWithYearEnd = shouldFetchDepreciationRows
          ? getYearsWithYearEndInDateFilter(filterOptions)
          : [];
        const depreciationRows = await Promise.allSettled(
          yearsWithYearEnd.map((year) =>
            api.kontax.fetchDepreciationTransactionRows({
              email,
              year,
            })
          )
        ).then((results) =>
          results.flatMap((result) =>
            result.status === "fulfilled" ? result.value : []
          )
        );

        const depreciationTransactions = mapDepreciationRowsToTransactions({
          rows: depreciationRows,
          email,
        });
        const transactions = mergeDepreciationRowsIntoTransactions({
          transactions: data.transactions,
          depreciationRows: depreciationTransactions,
          sort,
        });

        setResponse({
          transactions,
          count: data.count,
        });
        showSuccessMessage();
      } catch (error) {
        const { message: title, description } = getServerErrorNotification(
          error,
          "Error while fetching transactions"
        );
        notification.error({
          message: title,
          ...(description ? { description } : {}),
        });
      } finally {
        destroyMessage(loadingKey);
      }
    }
  }, [
    requestFilters,
    pagination.page,
    pagination.pageSize,
    filterOptions,
    sort,
  ]);

  const refreshTransactionSplitView = useCallback(
    (transactionId: string, state: TransactionSplitState) => {
      const updatedTransactions = response.transactions?.map((transaction) => {
        if (transaction.id === transactionId) {
          return { ...transaction, splits: state.splits };
        }

        return transaction;
      });

      updatedTransactions &&
        setResponse((prev) => ({
          ...prev,
          transactions: updatedTransactions as ITransaction[],
        }));
    },
    [response.transactions]
  );

  const refreshTransaction = useCallback(async (transactionId: string) => {
    try {
      const transaction = await api.kontax.fetchTransactionById(transactionId);
      if (!transaction) return;

      setResponse((prev) => {
        const prevTransactions = prev.transactions || [];
        const nextTransactions = prevTransactions.map((t) =>
          t.id === transaction.id ? transaction : t
        );

        return {
          ...prev,
          transactions: nextTransactions,
        };
      });
    } catch (error) {
      const { message: title, description } = getServerErrorNotification(
        error,
        "Error while refreshing the transaction."
      );
      notification.error({
        message: title,
        ...(description ? { description } : {}),
      });
    }
  }, []);

  const [upsertTransactionSplit] = useLazyRequest(
    async (transactionId: string, state: TransactionSplitState) => {
      try {
        if (state.isValid) {
          const methodName = state.isNew
            ? "createTransactionSplit"
            : "updateTransactionSplit";
          await api.kontax[methodName](
            transactionId,
            state.splits as Array<TransactionSplit>
          );

          await refreshTransaction(transactionId);
        }
      } catch (error) {
        const { message: title, description } = getServerErrorNotification(
          error,
          "Error while split transaction"
        );
        notification.error({
          message: title,
          ...(description ? { description } : {}),
        });
      }
    }
  );

  const [deleteTransactionSplit] = useLazyRequest(
    async (transactionId: string, state: TransactionSplitState) => {
      try {
        if (!state.isNew) {
          await api.kontax.deleteTransactionSplit(transactionId);
          await refreshTransaction(transactionId);
        }
      } catch (error) {
        const { message: title, description } = getServerErrorNotification(
          error,
          "Error while delete transaction"
        );
        notification.error({
          message: title,
          ...(description ? { description } : {}),
        });
      }
    }
  );

  const [upsertKontaxTransactions] = useUpsertTransactionsMutation();

  const updateTransactionsMeta = async (
    payload: Array<KontaxTransactionMeta>
  ) => {
    resetSelectedTransactions();

    const mappedTransactions = payload.map((payload) => {
      let { categoryCodeMeta, vatCategoryCodeMeta, ...rest } = payload;
      return {
        categoryCodeSuggestionSource: categoryCodeMeta?.suggestionSource,
        ...rest,
      };
    });

    const shouldRefetchAllTransactions = payload.some(
      (t) => t.categoryCode === "0000"
    );

    try {
      const upsertResponse = await upsertKontaxTransactions({
        variables: {
          transactions: mappedTransactions as KontaxTransactionUpsertInput[],
        },
      });

      const updatedTransactions =
        upsertResponse?.data?.upsertKontaxTransactions?.transactions || [];

      // transaction can be removed from DB eventually. For example: in case agent un-verify DATEV transaction.
      // in that case, we simply exclude it from the transactions.
      if (updatedTransactions.length !== mappedTransactions.length) {
        for (const mappedTransaction of mappedTransactions) {
          if (
            !updatedTransactions.some(
              (updatedTransaction) =>
                mappedTransaction.id === updatedTransaction.id
            )
          ) {
            setResponse((prev) => ({
              ...prev,
              transactions:
                prev.transactions?.filter(
                  (transaction) => transaction.id !== mappedTransaction.id
                ) || null,
            }));
          }
        }
      }

      // Exchange old transactions with updated ones.
      setResponse((currentResponse) => {
        const changedTransactions = [];
        for (const updatedTransaction of updatedTransactions) {
          const index = _.findIndex(currentResponse.transactions, [
            "id",
            updatedTransaction.id,
          ]);
          if (index > -1) {
            const currentTransaction = currentResponse.transactions?.[index];
            changedTransactions[index] = {
              ...updatedTransaction,
              assets: currentTransaction?.assets || updatedTransaction.assets,
              splits: updatedTransaction.splits?.map((updatedSplit) => {
                const currentSplit = currentTransaction?.splits?.find(
                  (currentSplit) => currentSplit.id === updatedSplit.id
                );
                return {
                  ...currentSplit,
                  ...updatedSplit,
                };
              }),
            };
          }
        }

        return {
          ...currentResponse,
          transactions: Object.assign(
            [],
            currentResponse.transactions ?? [],
            changedTransactions
          ),
        };
      });

      if (shouldRefetchAllTransactions) {
        await fetchTransactions();
      }

      return true;
    } catch (error) {
      await fetchTransactions();
      const { message: title, description } = getServerErrorNotification(
        error,
        "Error while updating transaction"
      );
      notification.error({
        message: title,
        ...(description ? { description } : {}),
      });
      return false;
    }
  };

  const refreshTransactionAssets = useCallback(
    async (transactionId: string, assets: Array<Asset>) => {
      const transaction = response.transactions?.find(
        (t) => t.id === transactionId
      );
      const shouldRefetch = shouldRefetchTransactionOnAssetUpload(transaction);

      if (shouldRefetch) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        await refreshTransaction(transactionId);
      } else {
        const updatedTransactions = response.transactions?.map(
          (transaction) => {
            if (transaction.id === transactionId) {
              return { ...transaction, assets };
            }

            return transaction;
          }
        );

        updatedTransactions &&
          setResponse((prev) => ({
            ...prev,
            transactions: updatedTransactions as ITransaction[],
          }));
      }
    },
    [response.transactions, refreshTransaction]
  );

  const handleSortChange = useCallback(
    (field: string, direction: "asc" | "desc" | null) => {
      if (direction) {
        setSort({
          field,
          direction: direction.toUpperCase(),
        });
      } else {
        setSort(undefined);
      }
    },
    []
  );

  const handleFilterChange = useCallback((field: string, value: any) => {
    if (field === "assets") {
      setAssetsFilter(value);
    } else if (field === "internalNote") {
      setNoteFilters((prev) => ({
        ...prev,
        internalNote: value,
      }));
    } else if (field === "personalNote") {
      setNoteFilters((prev) => ({
        ...prev,
        personalNote: value,
      }));
    } else if (field === "verified") {
      setVerifiedFilter(value);
    } else if (field === "verifiedByML") {
      setVerifiedByMLFilter(value);
    } else if (field === "submissionStatus") {
      setSubmissionStatusFilter(value);
    } else if (field === "currency") {
      setCurrencyFilter(value ?? undefined);
    }
  }, []);

  useRequest(fetchTransactions);
  return (
    <TransactionsListViewWrapper>
      <TransactionsTable
        email={filterOptions.email}
        updateTransactionsMeta={updateTransactionsMeta}
        transactions={response.transactions}
        transactionsCount={response.count}
        requestFilters={requestFilters}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        onSortChange={handleSortChange}
        onFilterChange={handleFilterChange}
        currency={currencyFilter}
        currencyOptions={currencyOptions}
        accountsById={accountsById}
        assetsFilter={assetsFilter}
        noteFilters={noteFilters}
        verifiedFilter={verifiedFilter}
        verifiedByMLFilter={verifiedByMLFilter}
        submissionStatusFilter={submissionStatusFilter}
        updateTransactionsState={setResponse}
        onCsvImportSuccess={fetchTransactions}
        onTransactionRefresh={refreshTransaction}
        onSplitSubmit={(...args) => {
          refreshTransactionSplitView(...args);
          upsertTransactionSplit(...args);
        }}
        onSplitDelete={(...args) => {
          refreshTransactionSplitView(...args);
          deleteTransactionSplit(...args);
        }}
        onAssetsUpdate={refreshTransactionAssets}
        setSelection={setSelection}
        selectedTransactions={selectedTransactions}
        hasSelectedVerifiedTransactionsOnly={
          hasSelectedVerifiedTransactionsOnly
        }
        verifySelectedTransactions={verifySelectedTransactions}
        isNested={isNested}
      />
    </TransactionsListViewWrapper>
  );
};

export default TransactionList;
